import React, { useEffect, useRef, useState } from "react";
import { Terminal, Bot, Zap, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * AI Hunter Console — Step 11.
 * Terminal-style live feed of AI target classification, outreach dispatch,
 * and multi-tier conversion events.
 *
 * Pulls /api/tgc/outreach/console/feed every 15s (auto-refresh).
 */
export default function AiHunterConsole({ limit = 24, style = {} }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastAt, setLastAt] = useState(null);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/tgc/outreach/console/feed?limit=${limit}`);
      if (!r.ok) return;
      const j = await r.json();
      setEvents(Array.isArray(j.events) ? j.events : []);
      setLastAt(j.generated_at);
    } catch (_) { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [limit]);

  const colorFor = (ev) => {
    if (ev.type === "classify") {
      if (ev.tier === "MERCHANT_ENTERPRISE") return "#BC13FE";
      if (ev.tier === "MERCHANT_RETAIL") return "#FFD700";
      return "#00F2FF";
    }
    if (ev.type === "dispatch") return ev.status === "sent" ? "#10F4A7" : "#8B94B8";
    if (ev.type === "conversion") return "#10F4A7";
    return "#8B94B8";
  };

  return (
    <div data-testid="ai-hunter-console"
      style={{
        background: "#050914", border: "1px solid rgba(0,242,255,0.30)",
        borderRadius: 14, overflow: "hidden", boxShadow: "0 0 40px rgba(0,242,255,0.08)",
        ...style,
      }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 18px", background: "linear-gradient(90deg, rgba(0,242,255,0.10), rgba(188,19,254,0.08))",
        borderBottom: "1px solid rgba(0,242,255,0.20)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Terminal size={16} color="#00F2FF" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1, color: "#00F2FF" }}>
              AI HUNTER CONSOLE
            </div>
            <div style={{ fontSize: 10, color: "#5F6884", marginTop: 2 }}>
              Live target classification · dispatch · conversion stream
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, background: "#10F4A7", animation: "pulse 1.2s infinite" }} />
            <span style={{ fontSize: 10, color: "#10F4A7", letterSpacing: 1 }}>LIVE · 15s</span>
          </div>
          <button data-testid="ai-hunter-refresh" onClick={load}
            style={{ background: "transparent", border: "1px solid rgba(0,242,255,0.35)", color: "#00F2FF", padding: "4px 8px", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
            <RefreshCw size={11} /> Reload
          </button>
        </div>
      </div>

      {/* Terminal body */}
      <div ref={scrollRef}
        data-testid="ai-hunter-console-body"
        style={{
          fontFamily: "SFMono-Regular, 'Menlo', 'Monaco', 'Consolas', monospace",
          fontSize: 12, lineHeight: 1.55, padding: 16,
          background: "#050914",
          minHeight: 240, maxHeight: 380, overflowY: "auto",
          color: "#E7ECFF",
        }}>
        {loading && events.length === 0 && (
          <div style={{ color: "#5F6884" }}>
            <Bot size={12} style={{ verticalAlign: "middle", marginRight: 6 }} />
            Booting scouting network…
          </div>
        )}
        {!loading && events.length === 0 && (
          <div style={{ color: "#5F6884" }}>
            [AI] Waiting for the next targeting cycle. Add leads or run outreach ticks to populate the stream.
          </div>
        )}
        {events.map((ev, i) => {
          const c = colorFor(ev);
          const ts = ev.ts ? new Date(ev.ts).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "--:--:--";
          const icon = ev.type === "classify" ? <Bot size={12} /> :
                       ev.type === "dispatch" ? <Zap size={12} /> :
                       <Terminal size={12} />;
          return (
            <div key={i} data-testid={`ai-hunter-line-${ev.type}`}
              style={{ display: "flex", gap: 10, padding: "3px 0", alignItems: "flex-start" }}>
              <span style={{ color: "#5F6884", flexShrink: 0 }}>{ts}</span>
              <span style={{ color: c, flexShrink: 0, marginTop: 2 }}>{icon}</span>
              <span style={{ color: c, wordBreak: "break-word" }}>{ev.line}</span>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{
        display: "flex", justifyContent: "space-between", padding: "8px 16px",
        borderTop: "1px solid rgba(0,242,255,0.15)", background: "rgba(5,9,20,0.9)",
        fontSize: 10, color: "#5F6884",
      }}>
        <span>Tiers: <b style={{ color: "#00F2FF" }}>PROVIDER $10</b> · <b style={{ color: "#FFD700" }}>RETAIL $50</b> · <b style={{ color: "#BC13FE" }}>ENTERPRISE $250</b></span>
        <span>{lastAt ? `sync ${new Date(lastAt).toLocaleTimeString("ru-RU")}` : ""}</span>
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.35} }`}</style>
    </div>
  );
}
